/* eslint-disable react/display-name */
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useAppContext } from 'contexts/AppContext';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { getDetailsSheet } from 'utils/common';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useSwimlaneActions } from 'hooks/useApiActions';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useBudgetQueries } from 'utils/api/queries';
import { useTopLevelSectionMutation } from 'utils/api/mutations';
import DeleteConfirmModal from 'components/DeleteConfirmModal';
import Button from 'components/Button';
import Modal from 'components/Modal';
import { formatCurrency, toastStyle } from 'utils/helper';

// Import extracted components and hooks
import SwimlaneItem from './components/SwimlaneItem';
import AddItemSection from './components/AddItemSection';
import { useSwimlaneItemActions } from './hooks/useSwimlaneItemActions';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import ContractualChargesSection from './ContractualChargesSection';
import FilmIncentivesSection from './FilmIncentivesSection';
import { Icon } from 'utils/icons';
import {
  SWIMLANE_COLUMN_DEFAULT_WIDTH,
  SWIMLANE_COLUMN_MAX_WIDTH,
  SWIMLANE_COLUMN_MIN_WIDTH,
  getSwimlaneColumnWidthKey,
  normalizeSwimlaneColumnWidth,
  readStoredSwimlaneColumnWidth,
  writeStoredSwimlaneColumnWidth,
} from './columnWidthStorage';
import {
  getCategoryMenuItems as resolveCategoryMenuItems,
  insertCategoryRowsAdjacent,
  isCategoryMenuEvent,
} from './categoryMenuActions';

const DETAIL_SHEET_KEYS = [
  'swimlane_detail_sheets',
  'swimlane_item_detail_sheets',
  'line_item_detail_sheets',
  'detail_sheets',
];

const CHILD_CATEGORY_KEYS = [
  'budget_swimlane_lineitem',
  'bs_lineitem_children',
  'children',
  'line_items',
];

const calculateRenderedCost = item => {
  // Always use the backend-calculated cost as the single source of truth
  // The backend cost includes both item_total and fringe_total
  if (item?.cost !== undefined && item?.cost !== null) {
    return Number(item.cost) || 0;
  }

  // Fallback: calculate from detail sheets if cost is not available
  // This should only happen for new items that haven't been saved yet
  const detailSheets =
    item?.swimlane_detail_sheets || item?.swimlane_item_detail_sheets || [];

  if (detailSheets.length) {
    const fringeTotal = detailSheets
      .map(sheet => Number(sheet?.fringe_total) || 0)
      .reduce((acc, val) => acc + val, 0);

    const itemTotal = detailSheets
      .map(sheet => Number(sheet?.item_total) || 0)
      .reduce((acc, val) => acc + val, 0);

    return fringeTotal + itemTotal;
  }

  return 0;
};

const countDetailRows = node => {
  if (!node) return 0;

  const directDetails = DETAIL_SHEET_KEYS.reduce((count, key) => {
    const details = node?.[key];
    return count + (Array.isArray(details) ? details.length : 0);
  }, 0);

  const childDetails = CHILD_CATEGORY_KEYS.reduce((count, key) => {
    const children = node?.[key];
    if (!Array.isArray(children)) return count;

    return (
      count +
      children.reduce(
        (childCount, child) => childCount + countDetailRows(child),
        0
      )
    );
  }, 0);

  return directDetails + childDetails;
};

const findLineItemInTree = (lineItems, itemId) => {
  if (!Array.isArray(lineItems) || !itemId) return null;

  for (const lineItem of lineItems) {
    if (lineItem?.id?.toString() === itemId?.toString()) {
      return lineItem;
    }

    const childMatch = findLineItemInTree(lineItem?.children, itemId);
    if (childMatch) return childMatch;
  }

  return null;
};

const findCategoryInBudgetTree = ({ budgetData, type, itemId }) => {
  const swimlanes = budgetData?.budget_swimlane;
  if (!Array.isArray(swimlanes) || !itemId) return null;

  if (type === 'swimlane') {
    return (
      swimlanes.find(
        swimlane => swimlane?.id?.toString() === itemId?.toString()
      ) || null
    );
  }

  for (const swimlane of swimlanes) {
    const match = findLineItemInTree(
      swimlane?.budget_swimlane_lineitem,
      itemId
    );
    if (match) return match;
  }

  return null;
};

const isTextEditingTarget = target => {
  if (!target?.closest) return false;

  return Boolean(target.closest('input, textarea, select, [contenteditable]'));
};

const isSelectionModifierEvent = event =>
  Boolean(event?.shiftKey || event?.altKey || event?.metaKey || event?.ctrlKey);

const isTemporaryItem = itemId => /^NEW_ITEM_/.test(String(itemId || ''));

const TOP_SECTION_PREFIX = 'top-section-';
const INSERT_CATEGORY_ABOVE = 'add-category-above';
const INSERT_CATEGORY_BELOW = 'add-category-below';
const INSERT_CATEGORY_NAME = 'New Category';

const getSectionId = section => section?.id?.toString();

const getItemSectionId = item => {
  const section = item?.section;
  if (section === undefined || section === null || section === '') {
    return null;
  }

  return section?.id?.toString?.() || section?.toString();
};

const getItemId = item => item?.id?.toString?.() || item?.id;

const matchesItemId = (item, itemId) =>
  getItemId(item)?.toString() === itemId?.toString();

const clampIndex = (index, length) =>
  Math.min(Math.max(index, 0), Math.max(length, 0));

const withSequentialPositions = items =>
  items.map((item, position) => ({
    ...item,
    position,
  }));

const sortByPositionAndId = (a, b) => {
  const aPosition = Number(a?.position ?? 0);
  const bPosition = Number(b?.position ?? 0);
  if (aPosition !== bPosition) return aPosition - bPosition;
  return Number(a?.id ?? 0) - Number(b?.id ?? 0);
};

const buildTopLevelRows = (itemsWithCost, sections = []) => {
  const sortedSections = [...sections].sort(sortByPositionAndId);
  const knownSectionIds = new Set(sortedSections.map(getSectionId));
  const sortedItems = [...itemsWithCost].sort((a, b) =>
    sortByPositionAndId(a.item, b.item)
  );
  const rows = [];

  sortedSections.forEach(section => {
    const sectionId = getSectionId(section);
    sortedItems
      .filter(({ item }) => getItemSectionId(item) === sectionId)
      .forEach(entry => rows.push({ type: 'item', ...entry }));
    rows.push({ type: 'section', section });
  });

  sortedItems
    .filter(({ item }) => {
      const sectionId = getItemSectionId(item);
      return !sectionId || !knownSectionIds.has(sectionId);
    })
    .forEach(entry => rows.push({ type: 'item', ...entry }));

  return rows;
};

const rowsToRepositionPayload = rows => {
  const sectionPositions = [];
  const swimlanes = [];
  const pendingItems = [];

  const flushItems = sectionId => {
    pendingItems.forEach((item, position) => {
      swimlanes.push({
        id: item.id,
        section: sectionId,
        position,
      });
    });
    pendingItems.length = 0;
  };

  rows.forEach(row => {
    if (row.type === 'section') {
      flushItems(row.section.id);
      sectionPositions.push({
        id: row.section.id,
        position: sectionPositions.length,
      });
      return;
    }

    pendingItems.push(row.item);
  });

  flushItems(null);

  return {
    sections: sectionPositions,
    swimlanes,
  };
};

const rowsToPositionedItems = rows => {
  const pendingRows = [];
  const positionedItems = [];

  const flushRows = sectionId => {
    pendingRows.forEach((row, position) => {
      positionedItems.push({
        ...row.item,
        section: sectionId,
        position,
      });
    });
    pendingRows.length = 0;
  };

  rows.forEach(row => {
    if (row.type === 'section') {
      flushRows(row.section.id);
      return;
    }

    pendingRows.push(row);
  });

  flushRows(null);

  return positionedItems;
};

const applySectionPositions = (sections = [], sectionPositions = []) => {
  const positionById = new Map(
    sectionPositions.map(({ id, position }) => [id?.toString(), position])
  );

  return sections
    .map(section => {
      const sectionId = getSectionId(section);

      if (!positionById.has(sectionId)) {
        return section;
      }

      return {
        ...section,
        position: positionById.get(sectionId),
      };
    })
    .sort(sortByPositionAndId);
};

const getSectionOrderKey = sections =>
  [...(sections || [])].sort(sortByPositionAndId).map(getSectionId).join('|');

const moveRow = (rows, sourceIndex, destinationIndex) => {
  const nextRows = Array.from(rows);
  const [removed] = nextRows.splice(sourceIndex, 1);
  nextRows.splice(destinationIndex, 0, removed);
  return nextRows;
};

const moveTopLevelRow = (rows, sourceIndex, destinationIndex) =>
  moveRow(rows, sourceIndex, destinationIndex);

const areSequentialIndexes = indexes =>
  indexes.length > 0 &&
  indexes.every((index, itemIndex) => {
    if (itemIndex === 0) return true;
    return index === indexes[itemIndex - 1] + 1;
  });

const moveRowsAsBlock = (rows, rowIndexes, destinationIndex) => {
  if (!areSequentialIndexes(rowIndexes)) return null;

  const blockStart = rowIndexes[0];
  const blockEnd = rowIndexes[rowIndexes.length - 1];
  if (destinationIndex >= blockStart && destinationIndex <= blockEnd) {
    return null;
  }

  const selectedRowIndexSet = new Set(rowIndexes);
  const movedRows = rows.filter((_row, index) =>
    selectedRowIndexSet.has(index)
  );
  const remainingRows = rows.filter(
    (_row, index) => !selectedRowIndexSet.has(index)
  );
  const insertionIndex =
    destinationIndex < blockStart
      ? destinationIndex
      : destinationIndex - rowIndexes.length + 1;

  const nextRows = [...remainingRows];
  nextRows.splice(clampIndex(insertionIndex, nextRows.length), 0, ...movedRows);
  return nextRows;
};

const TopLevelSectionHeader = ({
  section,
  index,
  subtotal,
  hasEditAccess,
  onOpenSettings,
}) => {
  const canEdit = hasEditAccess();

  const openSettings = () => {
    onOpenSettings(section);
  };

  const handleClick = event => {
    if (isTextEditingTarget(event.target)) {
      return;
    }

    event.stopPropagation();
    openSettings();
  };

  const handleKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openSettings();
  };

  return (
    <Draggable
      draggableId={`${TOP_SECTION_PREFIX}${section.id}`}
      index={index}
      isDragDisabled={!canEdit}
    >
      {provided => (
        <div
          className="top-level-section-header"
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
        >
          <div className="top-level-section-header-main">
            <Icon
              name="CaretUpIcon"
              className="top-level-section-header-caret"
            />
            <span className="top-level-section-header-name">
              {section.name}
            </span>
          </div>
          <div className="top-level-section-header-right">
            <span className="top-level-section-header-subtotal">
              {formatCurrency(subtotal || 0)}
            </span>
          </div>
        </div>
      )}
    </Draggable>
  );
};

TopLevelSectionHeader.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  subtotal: PropTypes.number.isRequired,
  hasEditAccess: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func.isRequired,
};

const SectionSettingsModal = ({
  show,
  section,
  subtotal,
  categories,
  draftName,
  canEdit,
  selectTitleOnOpen,
  onDraftNameChange,
  onSave,
  onDelete,
  onHide,
  isSaving,
  isDeleting,
}) => {
  const titleInputRef = useRef(null);

  useEffect(() => {
    if (!show || !canEdit || !selectTitleOnOpen) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [show, canEdit, selectTitleOnOpen, section?.id]);

  const handleSubmit = event => {
    event.preventDefault();
    onSave(section);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className="section-settings-modal"
      title="Section Settings"
      size="md"
      centered
    >
      <form className="section-settings-modal-body" onSubmit={handleSubmit}>
        <label className="section-settings-modal-label" htmlFor="sectionName">
          Section Title
        </label>
        <input
          ref={titleInputRef}
          id="sectionName"
          className="section-settings-modal-input"
          value={draftName}
          onChange={event => onDraftNameChange(event.target.value)}
          disabled={!canEdit}
          autoFocus
        />

        <div className="section-settings-modal-summary">
          <span>Section Total</span>
          <strong>{formatCurrency(subtotal || 0)}</strong>
        </div>

        <div className="section-settings-modal-categories">
          <div className="section-settings-modal-categories-title">
            Categories in this section
          </div>
          {categories.length ? (
            <ul>
              {categories.map(category => (
                <li key={category.item?.id || category.item?.name}>
                  <span className="section-settings-modal-category-name">
                    {category.item?.name || 'Untitled Category'}
                  </span>
                  <strong>{formatCurrency(category.renderedCost || 0)}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <div className="section-settings-modal-empty">
              No categories in this section.
            </div>
          )}
        </div>

        <div className="section-settings-modal-actions">
          {canEdit && (
            <Button
              label={
                <span className="section-settings-modal-delete-label">
                  <Icon name="TrashCanIcon" />
                  Delete Section
                </span>
              }
              variant="danger"
              className="section-settings-modal-delete"
              onClick={() => onDelete(section)}
              isDisabled={isDeleting}
            />
          )}
          <div className="section-settings-modal-primary-actions">
            <Button label="Cancel" variant="grey-outlined" onClick={onHide} />
            {canEdit && (
              <Button
                label="Save"
                type="submit"
                variant="primary"
                isDisabled={isSaving || !draftName.trim()}
              />
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

SectionSettingsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  section: PropTypes.object,
  subtotal: PropTypes.number,
  categories: PropTypes.arrayOf(PropTypes.object),
  draftName: PropTypes.string.isRequired,
  canEdit: PropTypes.bool.isRequired,
  selectTitleOnOpen: PropTypes.bool.isRequired,
  onDraftNameChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
};

SectionSettingsModal.defaultProps = {
  section: null,
  subtotal: 0,
  categories: [],
};

/**
 * Refactored Swimlane component with extracted components and hooks
 * This version demonstrates better separation of concerns and reusability
 */
const SwimlaneRefactored = forwardRef(
  (
    {
      swimlaneHeight,
      selectedItem,
      type,
      onKeyUp,
      editing,
      setEditing,
      swimlane,
      swimlaneIndex,
      handleAddSwimLanes,
      addItemSuggestions,
      addDetailedView,
      budgetId,
      idCat,
      clearSwimlaneInfo,
      presenceBreadcrumbs,
      budgetData,
      droppableId,
      categoryNestHoverKey,
      categoryNestReadyKey,
      activeSelectionColumnIndex,
      onCategorySelectionActivated,
      onColumnWidthChange,
      contextualCategorySuggestionRequest,
      suggestionScopeKey,
      onSuggestionDeleted,
      onCategoryColumnItemsChange,
      ...props
    },
    ref
  ) => {
    const { isUserLoggedIn, useReorderSwimlane, useReorderLineItem } =
      useAppContext();

    // Use Redux actions instead of direct API calls
    const { createSwimlane, getSwimlanesByTypeAndId } = useSwimlaneActions();

    const {
      swimlanes,
      setShowDetailedView,
      refetchSwimlaneCosts,
      refetchBudgetId,
      hasEditAccess,
      addedContextualSuggestions,
      setAddedContextualSuggestions,
      tabActive,
      setTabActive,
      sefFringeAppliedTotal,
      showDetailedView,
      showAccountNumbers,
    } = useHomeScreenContext();

    const isTempId = itemId => /^NEW_ITEM_/.test(itemId);
    const renderInsertCategoryIcon = direction => (
      <Icon
        name={direction === 'above' ? 'CaretUpIcon' : 'CaretDownIcon'}
        style={{ marginRight: '8px', width: '14px' }}
      />
    );

    const lineItemOptions = [
      {
        value: 'duplicate-item',
        label: 'Duplicate Category',
        icon: (
          <Icon
            name="CloneIcon"
            style={{ marginRight: '8px', width: '14px' }}
          />
        ),
      },
      {
        value: 'add-sub-category',
        label: 'Add Sub Category',
      },
      {
        value: 'delete-item',
        label: 'Delete Category',
        icon: (
          <Icon
            name="TrashCanIcon"
            style={{ marginRight: '8px', width: '14px' }}
          />
        ),
      },
      {
        value: 'divider',
      },
      {
        value: INSERT_CATEGORY_ABOVE,
        label: 'Add Category Above',
        icon: renderInsertCategoryIcon('above'),
      },
      {
        value: INSERT_CATEGORY_BELOW,
        label: 'Add Category Below',
        icon: renderInsertCategoryIcon('below'),
      },
    ];

    const itemKeyRef = useRef({ uniqueKeys: {}, counter: 0 });
    const [showAddItemInput, setShowAddItemInput] = useState(false);
    const [swimlaneItems, setSwimLaneItems] = useState([]);
    const [activeItem, setActiveItem] = useState();
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [selectionAnchorId, setSelectionAnchorId] = useState(null);
    const [subCategoryItem, setSubCategoryItem] = useState(null);
    const [categoryDeleteTarget, setCategoryDeleteTarget] = useState(null);
    const [sectionSettingsTarget, setSectionSettingsTarget] = useState(null);
    const [optimisticTopLevelSections, setOptimisticTopLevelSections] =
      useState(null);
    const [selectSectionTitleOnOpen, setSelectSectionTitleOnOpen] =
      useState(false);
    const [isDeletingCategory, setIsDeletingCategory] = useState(false);
    const [sectionNameDraft, setSectionNameDraft] = useState('');
    const [, setCatStore] = useState();
    const hasInitialTotalSet = useRef(false);
    const currentBudgetRef = useRef(budgetId);
    const contextualSuggestionCreatesRef = useRef({
      created: new Set(),
      inFlight: new Set(),
    });
    const contextualCategorySuggestionHandledRef = useRef(new Set());
    const pendingNameSelectCategoryIdsRef = useRef(new Set());
    const selectedItemId = budgetId || selectedItem?.id;
    const swimlaneColumnWidthKey = getSwimlaneColumnWidthKey(swimlaneIndex);
    const [swimlaneColumnWidth, setSwimlaneColumnWidth] = useState(
      () =>
        readStoredSwimlaneColumnWidth(budgetId, swimlaneColumnWidthKey) ||
        SWIMLANE_COLUMN_DEFAULT_WIDTH
    );
    const createTopLevelSectionMutation =
      useTopLevelSectionMutation.useCreateTopLevelSectionMutation();
    const updateTopLevelSectionMutation =
      useTopLevelSectionMutation.useUpdateTopLevelSectionMutation();
    const deleteTopLevelSectionMutation =
      useTopLevelSectionMutation.useDeleteTopLevelSectionMutation();
    const queryClient = useQueryClient();

    const swimlaneQueryPayload = useMemo(
      () => ({
        id: swimlaneIndex === 0 ? budgetId : selectedItem?.id,
        type,
      }),
      [budgetId, selectedItem?.id, swimlaneIndex, type]
    );

    useEffect(() => {
      setSwimlaneColumnWidth(
        readStoredSwimlaneColumnWidth(budgetId, swimlaneColumnWidthKey) ||
          SWIMLANE_COLUMN_DEFAULT_WIDTH
      );
    }, [budgetId, swimlaneColumnWidthKey]);

    const handleSwimlaneColumnResize = useCallback(
      (_event, data) => {
        const nextWidth = normalizeSwimlaneColumnWidth(data?.size?.width);
        if (!nextWidth) return;

        setSwimlaneColumnWidth(nextWidth);
        onColumnWidthChange?.();
      },
      [onColumnWidthChange]
    );

    const handleSwimlaneColumnResizeStop = useCallback(
      (_event, data) => {
        const nextWidth = normalizeSwimlaneColumnWidth(data?.size?.width);
        if (!nextWidth) return;

        setSwimlaneColumnWidth(nextWidth);
        writeStoredSwimlaneColumnWidth(
          budgetId,
          swimlaneColumnWidthKey,
          nextWidth
        );
        onColumnWidthChange?.();
      },
      [budgetId, onColumnWidthChange, swimlaneColumnWidthKey]
    );

    const setSwimlaneQueryCache = useCallback(
      nextItems => {
        if (!swimlaneQueryPayload?.id || !swimlaneQueryPayload?.type) return;

        queryClient.setQueryData(
          ['get-swimlanes-in-budget', { ...swimlaneQueryPayload }],
          previousData => {
            if (Array.isArray(previousData)) {
              return nextItems;
            }

            return {
              ...(previousData || {}),
              data: nextItems,
            };
          }
        );
      },
      [queryClient, swimlaneQueryPayload]
    );

    // Get swimlanes from Redux state
    const items = useSelector(state => {
      const selector = getSwimlanesByTypeAndId();
      return selector(
        state,
        type,
        swimlaneIndex === 0 ? budgetId : selectedItem?.id
      );
    });

    // Fetch from React Query to get updated data from backend
    const {
      data: queryData,
      refetch,
      dataUpdatedAt,
    } = useBudgetQueries.useSwimlaneQuery({
      enabled: Boolean(
        isUserLoggedIn && type && selectedItemId && !isTempId(selectedItemId)
      ),
      payload: swimlaneQueryPayload,
    });
    const queryItems = queryData?.data;

    // Always prioritize React Query data (gets updated from backend)
    // Only fall back to Redux if React Query data is not available yet
    // Use a key based on dataUpdatedAt to force re-render when query updates
    const currentItems = queryItems !== undefined ? queryItems : items;
    const categoryColumnFingerprint = useMemo(() => {
      if (!Array.isArray(queryItems)) return undefined;

      return queryItems
        .map(item =>
          [
            item?.id,
            String(item?.name || '')
              .trim()
              .toLowerCase(),
          ].join(':')
        )
        .sort()
        .join('|');
    }, [queryItems]);

    useEffect(() => {
      if (typeof categoryColumnFingerprint !== 'string') return;

      onCategoryColumnItemsChange?.({
        budgetId,
        suggestionScopeKey,
        fingerprint: categoryColumnFingerprint,
      });
    }, [
      budgetId,
      categoryColumnFingerprint,
      onCategoryColumnItemsChange,
      suggestionScopeKey,
    ]);

    // Initialize custom hooks
    const {
      handleAddItem,
      handleItemUpdate,
      handleItemNameChange,
      handleDropdownAction,
      handleDeleteItem,
      newItemCache,
    } = useSwimlaneItemActions({
      type,
      budgetId,
      selectedItem,
      swimlane,
      swimlaneItems,
      setSwimLaneItems,
      refetchBudgetId,
      setShowDetailedView,
      setEditing,
      swimlanes,
      refetchSwimlaneCosts,
      itemKeyRef,
      onSwimlaneDeleted: clearSwimlaneInfo,
    });

    const { handleDragEnd } = useDragAndDrop({
      swimlaneItems,
      setSwimLaneItems,
      refetchBudgetId,
      queryPayload: swimlaneQueryPayload,
    });

    const handleItemAccountNumberChange = useCallback(
      item => event => {
        const accountNumber = (event?.target?.value || '').slice(0, 14);
        if ((item?.account_number || '') !== accountNumber) {
          handleItemUpdate(item.id, 'account_number', accountNumber);
        }
      },
      [handleItemUpdate]
    );

    const swimlaneItemsWithCost = useMemo(() => {
      if (!Array.isArray(swimlaneItems)) {
        return [];
      }
      return swimlaneItems.map(item => ({
        item,
        renderedCost: calculateRenderedCost(item),
      }));
    }, [swimlaneItems]);

    const topLevelSections = useMemo(() => {
      if (swimlaneIndex !== 0) {
        return [];
      }

      if (Array.isArray(optimisticTopLevelSections)) {
        return optimisticTopLevelSections;
      }

      return Array.isArray(budgetData?.top_level_sections)
        ? budgetData.top_level_sections
        : [];
    }, [
      budgetData?.top_level_sections,
      optimisticTopLevelSections,
      swimlaneIndex,
    ]);

    useEffect(() => {
      if (
        !Array.isArray(optimisticTopLevelSections) ||
        !Array.isArray(budgetData?.top_level_sections)
      ) {
        return;
      }

      if (
        getSectionOrderKey(optimisticTopLevelSections) ===
        getSectionOrderKey(budgetData.top_level_sections)
      ) {
        setOptimisticTopLevelSections(null);
      }
    }, [budgetData?.top_level_sections, optimisticTopLevelSections]);

    const topLevelRows = useMemo(() => {
      if (swimlaneIndex !== 0) {
        return [];
      }

      return buildTopLevelRows(swimlaneItemsWithCost, topLevelSections);
    }, [swimlaneIndex, swimlaneItemsWithCost, topLevelSections]);

    const generateItemId = useCallback(
      item => {
        const itemIdentity =
          item?.category_id ??
          item?.budget_category ??
          item?.legacy_category_id ??
          item?.id;
        const scopedIdentity =
          itemIdentity !== undefined && itemIdentity !== null
            ? itemIdentity
            : `${item?.name || 'item'}-${item?.position || 'new'}`;
        const itemKey = [
          type,
          swimlaneIndex,
          selectedItemId || 'root',
          scopedIdentity,
        ].join('-');

        if (!itemKeyRef.current.uniqueKeys[itemKey]) {
          itemKeyRef.current.uniqueKeys[itemKey] = itemKey;
        }

        return itemKeyRef.current.uniqueKeys[itemKey];
      },
      [selectedItemId, swimlaneIndex, type]
    );

    const renderedCategoryRows = useMemo(() => {
      const rows =
        swimlaneIndex === 0
          ? topLevelRows
          : swimlaneItemsWithCost?.map(entry => ({
              type: 'item',
              ...entry,
            }));

      return (rows || []).map(row =>
        row.type === 'item'
          ? {
              ...row,
              draggableId: generateItemId(row.item),
            }
          : row
      );
    }, [generateItemId, swimlaneIndex, swimlaneItemsWithCost, topLevelRows]);

    const categoryItemRows = useMemo(
      () =>
        (renderedCategoryRows || [])
          .map((row, rowIndex) =>
            row.type === 'item'
              ? {
                  ...row,
                  itemId: getItemId(row.item)?.toString(),
                  rowIndex,
                }
              : null
          )
          .filter(row => row?.itemId && !isTemporaryItem(row.itemId)),
      [renderedCategoryRows]
    );

    const categoryItemIdsInOrder = useMemo(
      () => categoryItemRows.map(row => row.itemId),
      [categoryItemRows]
    );

    const selectedCategoryIdSet = useMemo(
      () => new Set(selectedCategoryIds),
      [selectedCategoryIds]
    );

    const selectedCategoryRows = useMemo(
      () =>
        categoryItemRows.filter(row => selectedCategoryIdSet.has(row.itemId)),
      [categoryItemRows, selectedCategoryIdSet]
    );

    const getMenuTargetItems = useCallback(
      item =>
        resolveCategoryMenuItems({
          clickedItem: item,
          selectedRows: selectedCategoryRows,
        }),
      [selectedCategoryRows]
    );

    const selectedCategoryTotal = useMemo(
      () =>
        selectedCategoryRows.reduce(
          (total, row) => total + (Number(row.renderedCost) || 0),
          0
        ),
      [selectedCategoryRows]
    );

    const clearCategorySelection = useCallback(
      ({ notify = true } = {}) => {
        setSelectedCategoryIds([]);
        setSelectionAnchorId(null);
        if (notify) {
          onCategorySelectionActivated?.(null);
        }
      },
      [onCategorySelectionActivated]
    );

    useEffect(() => {
      if (
        activeSelectionColumnIndex === swimlaneIndex ||
        !selectedCategoryIds.length
      ) {
        return;
      }

      clearCategorySelection({ notify: false });
    }, [
      activeSelectionColumnIndex,
      clearCategorySelection,
      selectedCategoryIds.length,
      swimlaneIndex,
    ]);

    useEffect(() => {
      const availableIds = new Set(categoryItemIdsInOrder);
      setSelectedCategoryIds(currentSelection => {
        const nextSelection = currentSelection.filter(id =>
          availableIds.has(id)
        );
        return nextSelection.length === currentSelection.length
          ? currentSelection
          : nextSelection;
      });

      if (selectionAnchorId && !availableIds.has(selectionAnchorId)) {
        setSelectionAnchorId(null);
      }
    }, [categoryItemIdsInOrder, selectionAnchorId]);

    const handleCategorySelectionClick = useCallback(
      (item, event) => {
        const itemId = getItemId(item)?.toString();
        if (!itemId || isTemporaryItem(itemId)) return;

        event?.preventDefault?.();
        event?.stopPropagation?.();
        onCategorySelectionActivated?.(swimlaneIndex);

        const activeItemId = getItemId(activeItem)?.toString();
        const hasActiveItemInColumn =
          activeItemId && categoryItemIdsInOrder.includes(activeItemId);

        if (event?.shiftKey) {
          const anchorId =
            selectionAnchorId ||
            (hasActiveItemInColumn ? activeItemId : null) ||
            itemId;
          const anchorIndex = categoryItemIdsInOrder.indexOf(anchorId);
          const itemIndex = categoryItemIdsInOrder.indexOf(itemId);

          if (anchorIndex === -1 || itemIndex === -1) {
            setSelectedCategoryIds([itemId]);
            setSelectionAnchorId(itemId);
            return;
          }

          const startIndex = Math.min(anchorIndex, itemIndex);
          const endIndex = Math.max(anchorIndex, itemIndex);
          setSelectedCategoryIds(
            categoryItemIdsInOrder.slice(startIndex, endIndex + 1)
          );
          setSelectionAnchorId(anchorId);
          return;
        }

        let baseIds = selectedCategoryIds;
        if (!baseIds.length && hasActiveItemInColumn) {
          baseIds = [activeItemId];
        }
        const nextIdSet = new Set(baseIds);
        if (nextIdSet.has(itemId)) {
          nextIdSet.delete(itemId);
        } else {
          nextIdSet.add(itemId);
        }

        const nextSelection = categoryItemIdsInOrder.filter(id =>
          nextIdSet.has(id)
        );
        setSelectedCategoryIds(nextSelection);
        setSelectionAnchorId(itemId);
        if (!nextSelection.length) {
          onCategorySelectionActivated?.(null);
        }
      },
      [
        activeItem,
        categoryItemIdsInOrder,
        onCategorySelectionActivated,
        selectedCategoryIds,
        selectionAnchorId,
        swimlaneIndex,
      ]
    );

    const getSelectedCategoryGroup = useCallback(
      draggableId => {
        const draggedRow = categoryItemRows.find(
          row => row.draggableId === draggableId
        );
        if (!draggedRow) return null;

        if (
          selectedCategoryIds.length <= 1 ||
          !selectedCategoryIdSet.has(draggedRow.itemId)
        ) {
          return {
            ids: [draggedRow.itemId],
            isGrouped: false,
            isSequential: true,
            items: [draggedRow.item],
            rowIndexes: [draggedRow.rowIndex],
            total: Number(draggedRow.renderedCost) || 0,
          };
        }

        const selectedRows = categoryItemRows.filter(row =>
          selectedCategoryIdSet.has(row.itemId)
        );
        const rowIndexes = selectedRows.map(row => row.rowIndex);

        return {
          ids: selectedRows.map(row => row.itemId),
          isGrouped: selectedRows.length > 1,
          isSequential: areSequentialIndexes(rowIndexes),
          items: selectedRows.map(row => row.item),
          rowIndexes,
          total: selectedRows.reduce(
            (total, row) => total + (Number(row.renderedCost) || 0),
            0
          ),
        };
      },
      [categoryItemRows, selectedCategoryIdSet, selectedCategoryIds.length]
    );

    const handleNestedCategoryDragEnd = useCallback(
      result => {
        if (!result.destination) return;

        const selectedGroup = getSelectedCategoryGroup(result.draggableId);
        if (!selectedGroup?.isGrouped) {
          handleDragEnd(result);
          return;
        }

        if (!selectedGroup.isSequential) {
          toast.error(
            'Select adjacent categories to move them together.',
            toastStyle
          );
          return;
        }

        const reorderedRows = moveRowsAsBlock(
          swimlaneItems.map(item => ({ item, type: 'item' })),
          selectedGroup.rowIndexes,
          result.destination.index
        );
        if (!reorderedRows) return;

        const reorderedItems = withSequentialPositions(
          reorderedRows.map(row => row.item)
        );
        const payload = reorderedItems.map(({ id }, position) => ({
          id,
          position,
        }));

        setSwimLaneItems(reorderedItems);
        setSwimlaneQueryCache(reorderedItems);

        useReorderLineItem.mutate(
          { line_items: payload },
          {
            onSuccess: () => {
              refetchBudgetId?.();
            },
            onError: () => {
              refetch?.();
              refetchBudgetId?.();
            },
          }
        );
      },
      [
        getSelectedCategoryGroup,
        handleDragEnd,
        refetch,
        refetchBudgetId,
        setSwimLaneItems,
        setSwimlaneQueryCache,
        swimlaneItems,
        useReorderLineItem,
      ]
    );

    const sectionSubtotalById = useMemo(() => {
      let pendingSubtotal = 0;

      return topLevelRows.reduce((totals, row) => {
        if (row.type === 'item') {
          pendingSubtotal += row.renderedCost || 0;
          return totals;
        }

        const sectionId = getSectionId(row.section);
        const nextTotals = {
          ...totals,
          [sectionId]: pendingSubtotal,
        };
        pendingSubtotal = 0;
        return nextTotals;
      }, {});
    }, [topLevelRows]);

    const sectionCategoriesById = useMemo(() => {
      let pendingCategories = [];

      return topLevelRows.reduce((categoriesById, row) => {
        if (row.type === 'item') {
          pendingCategories.push(row);
          return categoriesById;
        }

        const sectionId = getSectionId(row.section);
        const nextCategoriesById = {
          ...categoriesById,
          [sectionId]: pendingCategories,
        };
        pendingCategories = [];
        return nextCategoriesById;
      }, {});
    }, [topLevelRows]);

    const handleTopLevelDragEnd = useCallback(
      result => {
        if (!result.destination) return;
        const { source, destination } = result;
        if (source.index === destination.index) return;

        const selectedGroup = getSelectedCategoryGroup(result.draggableId);
        if (selectedGroup?.isGrouped && !selectedGroup.isSequential) {
          toast.error(
            'Select adjacent categories to move them together.',
            toastStyle
          );
          return;
        }

        const reorderedRows = selectedGroup?.isGrouped
          ? moveRowsAsBlock(
              topLevelRows,
              selectedGroup.rowIndexes,
              destination.index
            )
          : moveTopLevelRow(topLevelRows, source.index, destination.index);
        if (!reorderedRows) return;

        const payload = rowsToRepositionPayload(reorderedRows);
        const positionedItems = rowsToPositionedItems(reorderedRows);
        const positionedSections = applySectionPositions(
          topLevelSections,
          payload.sections
        );

        setOptimisticTopLevelSections(positionedSections);
        setSwimLaneItems(positionedItems);
        setSwimlaneQueryCache(positionedItems);

        useReorderSwimlane.mutate(payload, {
          onSuccess: () => {
            refetch?.();
            refetchBudgetId?.();
          },
          onError: () => {
            setOptimisticTopLevelSections(null);
            refetch?.();
            refetchBudgetId?.();
          },
        });
      },
      [
        getSelectedCategoryGroup,
        topLevelRows,
        setSwimLaneItems,
        setSwimlaneQueryCache,
        topLevelSections,
        useReorderSwimlane,
        refetch,
        refetchBudgetId,
      ]
    );

    const handleCreateTopLevelSection = useCallback(async () => {
      if (!budgetId || !hasEditAccess()) return;

      try {
        const response = await createTopLevelSectionMutation.mutateAsync({
          budget: budgetId,
          name: 'New Section',
          position: topLevelSections.length,
        });
        const section = response?.data;
        if (section?.id) {
          const rowsWithNewSection = [
            ...topLevelRows,
            { type: 'section', section },
          ];
          const payload = rowsToRepositionPayload(rowsWithNewSection);
          const positionedItems = rowsToPositionedItems(rowsWithNewSection);

          setSwimLaneItems(positionedItems);
          setSwimlaneQueryCache(positionedItems);
          await useReorderSwimlane.mutateAsync(payload);
          setSectionSettingsTarget(section);
          setSectionNameDraft(section.name || 'New Section');
          setSelectSectionTitleOnOpen(true);
        }
        refetchBudgetId?.();
      } catch (error) {
        console.error('Failed to create top level section', error);
      }
    }, [
      budgetId,
      createTopLevelSectionMutation,
      hasEditAccess,
      refetchBudgetId,
      setSwimLaneItems,
      setSwimlaneQueryCache,
      topLevelSections.length,
      topLevelRows,
      useReorderSwimlane,
    ]);

    const handleOpenSectionSettings = useCallback(section => {
      if (!section?.id) return;
      setSelectSectionTitleOnOpen(false);
      setSectionSettingsTarget(section);
      setSectionNameDraft(section.name || '');
    }, []);

    const handleCloseSectionSettings = useCallback(() => {
      setSectionSettingsTarget(null);
      setSectionNameDraft('');
      setSelectSectionTitleOnOpen(false);
    }, []);

    const handleSaveSection = useCallback(
      async section => {
        const nextName = sectionNameDraft.trim();

        if (!section?.id || !nextName) {
          handleCloseSectionSettings();
          return;
        }

        if (nextName === section.name) {
          handleCloseSectionSettings();
          return;
        }

        try {
          await updateTopLevelSectionMutation.mutateAsync({
            id: section.id,
            name: nextName,
          });
          refetchBudgetId?.();
        } catch (error) {
          console.error('Failed to update top level section', error);
        } finally {
          handleCloseSectionSettings();
        }
      },
      [
        handleCloseSectionSettings,
        refetchBudgetId,
        sectionNameDraft,
        updateTopLevelSectionMutation,
      ]
    );

    const handleDeleteTopLevelSection = useCallback(
      async section => {
        if (!section?.id) return;

        try {
          await deleteTopLevelSectionMutation.mutateAsync(section.id);
          handleCloseSectionSettings();
          refetch?.();
          refetchBudgetId?.();
        } catch (error) {
          console.error('Failed to delete top level section', error);
        }
      },
      [
        deleteTopLevelSectionMutation,
        handleCloseSectionSettings,
        refetch,
        refetchBudgetId,
      ]
    );

    useKeyboardNavigation({
      tabActive,
      setTabActive,
      swimlaneItems,
      swimlaneIndex,
      setCatStore,
    });

    // Calculate fringe applied total
    useEffect(() => {
      if (!currentItems || !Array.isArray(currentItems)) return;

      const totalFringeApplied = currentItems
        ?.map(itemInfo =>
          (
            itemInfo?.swimlane_detail_sheets ||
            itemInfo.swimlane_item_detail_sheets ||
            []
          )
            .map(item => Number(item?.fringe_total) || 0)
            .reduce((acc, val) => acc + val, 0)
        )
        .reduce((acc, val) => acc + val, 0);

      sefFringeAppliedTotal(totalFringeApplied);
    }, [currentItems, sefFringeAppliedTotal]);

    useImperativeHandle(ref, () => {
      return {
        items: () => swimlaneItems,
        handleItemClick: handleItemClick,
        handleDragEnd:
          swimlaneIndex === 0
            ? handleTopLevelDragEnd
            : handleNestedCategoryDragEnd,
        refetch: () => refetch?.(),
        setItems: nextItems => setSwimLaneItems(nextItems),
        getRows: () => renderedCategoryRows,
        getSelectedCategoryGroup,
        clearCategorySelection,
        getColumnContext: () => ({
          selectedItem,
          swimlane,
          swimlaneIndex,
          type,
        }),
      };
    });

    useEffect(() => {
      // Always update when currentItems or dataUpdatedAt changes
      // Create new object references to force React to detect the change
      // This ensures the component re-renders with updated costs from the backend
      if (Array.isArray(currentItems)) {
        // Create a new array with new object references
        // This forces React to detect the change even if the array reference is the same
        const newItems = currentItems.map(item => ({ ...item }));
        setSwimLaneItems(newItems);
      } else {
        setSwimLaneItems([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentItems, dataUpdatedAt]); // dataUpdatedAt ensures we detect query refetches

    useEffect(() => {
      if (Array.isArray(currentItems) && currentItems.length === 0) {
        hasInitialTotalSet.current = false;
      }
    }, [currentItems]);

    useEffect(() => {
      hasInitialTotalSet.current = false;
      currentBudgetRef.current = budgetId;
      // Clear active item when budget changes to prevent stale tabs
      setActiveItem(undefined);
      setOptimisticTopLevelSections(null);
    }, [budgetId]);

    // Removed: projectTotal should only come from backend budgetData.total_cost
    // The backend is the single source of truth for budget totals
    useEffect(() => {
      // Just mark as initialized, don't calculate total here
      if (!hasInitialTotalSet.current && currentItems && type === 'swimlane') {
        hasInitialTotalSet.current = true;
      }
    }, [currentItems, type]);

    useEffect(() => {
      if (swimlaneIndex !== 0) {
        return;
      }

      Object.keys(addedContextualSuggestions || {}).forEach(name => {
        const suggestionName = typeof name === 'string' ? name.trim() : '';
        if (!suggestionName) return;

        const normalizedName = suggestionName.toLowerCase();
        const alreadyInLane = swimlaneItems?.some(
          item =>
            typeof item?.name === 'string' &&
            item.name.trim().toLowerCase() === normalizedName
        );

        if (alreadyInLane) {
          contextualSuggestionCreatesRef.current.created.add(normalizedName);
          contextualSuggestionCreatesRef.current.inFlight.delete(
            normalizedName
          );
          setAddedContextualSuggestions(previous => {
            if (!previous?.[suggestionName]) return previous;
            const next = { ...previous };
            delete next[suggestionName];
            return next;
          });
          return;
        }

        if (
          contextualSuggestionCreatesRef.current.created.has(normalizedName) ||
          contextualSuggestionCreatesRef.current.inFlight.has(normalizedName)
        ) {
          return;
        }

        contextualSuggestionCreatesRef.current.inFlight.add(normalizedName);

        Promise.resolve(handleAddItem(suggestionName))
          .then(() => {
            contextualSuggestionCreatesRef.current.created.add(normalizedName);
            setAddedContextualSuggestions(previous => {
              if (!previous?.[suggestionName]) return previous;
              const next = { ...previous };
              delete next[suggestionName];
              return next;
            });
          })
          .catch(() => {
            contextualSuggestionCreatesRef.current.created.delete(
              normalizedName
            );
            setAddedContextualSuggestions(previous => {
              if (!previous?.[suggestionName]) return previous;
              const next = { ...previous };
              delete next[suggestionName];
              return next;
            });
          })
          .finally(() => {
            contextualSuggestionCreatesRef.current.inFlight.delete(
              normalizedName
            );
          });
      });
    }, [
      addedContextualSuggestions,
      handleAddItem,
      setAddedContextualSuggestions,
      swimlaneIndex,
      swimlaneItems,
    ]);

    const buildCategoryDeleteTarget = useCallback(
      itemsOrItem => {
        const categoryItems = (
          Array.isArray(itemsOrItem) ? itemsOrItem : [itemsOrItem]
        ).filter(item => item?.id && !isTemporaryItem(item.id));
        if (!categoryItems.length) return null;

        const itemDetails = categoryItems.map(item => {
          const budgetTreeItem =
            findCategoryInBudgetTree({
              budgetData,
              type,
              itemId: item.id,
            }) || item;

          return {
            item,
            lineItemCount: countDetailRows(budgetTreeItem),
            total: calculateRenderedCost(budgetTreeItem),
          };
        });

        return {
          item: itemDetails[0].item,
          items: itemDetails.map(detail => detail.item),
          categoryCount: itemDetails.length,
          lineItemCount: itemDetails.reduce(
            (total, detail) => total + detail.lineItemCount,
            0
          ),
          name:
            itemDetails.length > 1
              ? `${itemDetails.length} selected categories`
              : itemDetails[0].item?.name || 'this category',
          total: itemDetails.reduce((total, detail) => total + detail.total, 0),
        };
      },
      [budgetData, type]
    );

    // Removed: projectTotal should only come from backend budgetData.total_cost
    // The backend is the single source of truth for budget totals
    // No need to check for mismatches - trust the backend value

    const handleItemClick = useCallback(
      item => event => {
        if (isCategoryMenuEvent(event)) {
          event?.stopPropagation?.();
          return;
        }

        if (isSelectionModifierEvent(event)) {
          handleCategorySelectionClick(item, event);
          return;
        }

        clearCategorySelection();
        const detailSheet = getDetailsSheet(item, type);
        refetch?.();
        refetchBudgetId?.();
        const itemId = generateItemId(item);
        setActiveItem({
          ...item,
          rowId: itemId,
        });
        handleAddSwimLanes(
          swimlaneIndex,
          item,
          swimlaneIndex === 0 ? item.id : item.swimlane,
          detailSheet?.length > 0
        );
      },
      [
        clearCategorySelection,
        handleCategorySelectionClick,
        swimlaneIndex,
        handleAddSwimLanes,
        type,
        refetchBudgetId,
        refetch,
        generateItemId,
      ]
    );

    const addCategoryOnAction = useCallback(
      async (name, mytype, mySelectedItem, mySwimlane) => {
        const payload = {
          type: mytype,
          budgetId,
          selectedItem: mySelectedItem,
          name,
          swimlane: mySwimlane,
        };

        try {
          const response = await createSwimlane(payload).unwrap();
          refetchBudgetId?.();
          handleItemClick(mySelectedItem)();
          setEditing({ [response?.id]: true });
        } catch (otherError) {
          // Error handling is done in Redux action
        }
      },
      [budgetId, createSwimlane, refetchBudgetId, handleItemClick, setEditing]
    );

    useEffect(() => {
      if (subCategoryItem) {
        const item = subCategoryItem;
        setSubCategoryItem(null);
        const updatedItem = currentItems?.find(
          swimlaneItem => swimlaneItem.id === item.id
        );
        addCategoryOnAction(
          updatedItem?.name + ' Sub Category 2',
          swimlaneIndex === 0 ? 'line-item' : 'line-item-children',
          updatedItem,
          updatedItem?.swimlane || updatedItem?.id
        );
      }
    }, [subCategoryItem, currentItems, addCategoryOnAction, swimlaneIndex]);

    useEffect(() => {
      if (activeItem?.id && newItemCache.current?.[activeItem?.id]) {
        const newItem = newItemCache.current[activeItem?.id];
        delete newItemCache.current[activeItem?.id];
        handleItemClick(newItem)();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newItemCache.current, activeItem, budgetId]);

    // Select the swimlaneItems if there is only one
    useLayoutEffect(() => {
      if (currentBudgetRef.current !== budgetId) {
        return;
      }

      if (swimlaneItems?.length === 1 && swimlaneItems[0]?.id) {
        const itemId = generateItemId(swimlaneItems[0]);
        if (itemId !== activeItem?.rowId) {
          handleItemClick(swimlaneItems[0])();
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swimlaneItems, handleItemClick, activeItem, budgetId]);

    const hasDetailsSheet = useMemo(() => {
      const detailSheet =
        selectedItem &&
        getDetailsSheet(
          selectedItem,
          swimlaneIndex - 1 === 0 ? 'swimlane' : type
        );
      return detailSheet?.length > 0;
    }, [selectedItem, swimlaneIndex, type]);

    const handleAddItemClick = () => setShowAddItemInput(true);

    const handleAddItemWithCallback = async (name, addMore) => {
      try {
        const createdItem = await handleAddItem(name, addMore);
        if (createdItem && !addMore) setShowAddItemInput(false);
        return createdItem;
      } catch (_) {
        // The Redux action displays the final error and handleAddItem restores
        // the optimistic category. Keep the rejection out of DOM event handlers
        // so a transient local API restart never opens React's runtime overlay.
        return null;
      }
    };

    const deleteCategory = useCallback(
      async target => {
        const targetItems = target?.items || [target?.item].filter(Boolean);
        if (!targetItems.length) return;

        setIsDeletingCategory(true);

        try {
          let failedDeleteCount = 0;

          for (const item of targetItems) {
            const didDelete = await handleDeleteItem(item.id);
            if (didDelete) {
              onSuggestionDeleted?.(item.name, suggestionScopeKey);
            } else {
              failedDeleteCount += 1;
            }
          }

          if (failedDeleteCount) {
            toast.error(
              targetItems.length > 1
                ? 'Unable to delete all selected categories. Please try again.'
                : 'Unable to delete the category. Please try again.',
              toastStyle
            );
          }

          clearCategorySelection();
          refetch?.();
          refetchBudgetId?.();
          setCategoryDeleteTarget(null);
        } finally {
          setIsDeletingCategory(false);
        }
      },
      [
        clearCategorySelection,
        handleDeleteItem,
        onSuggestionDeleted,
        refetch,
        refetchBudgetId,
        suggestionScopeKey,
      ]
    );

    const requestCategoryDelete = useCallback(
      (item, { skipConfirmation = false } = {}) => {
        const target = buildCategoryDeleteTarget(item);
        if (!target) return;

        if (skipConfirmation) {
          deleteCategory(target);
          return;
        }

        setCategoryDeleteTarget(target);
      },
      [buildCategoryDeleteTarget, deleteCategory]
    );

    const persistNestedInsertedCategoryOrder = useCallback(
      async ({ item, placement, response }) => {
        const currentIndex = swimlaneItems.findIndex(swimlaneItem =>
          matchesItemId(swimlaneItem, item?.id)
        );
        if (currentIndex < 0) return;

        const insertIndex = clampIndex(
          currentIndex + (placement === 'below' ? 1 : 0),
          swimlaneItems.length
        );
        const positionedItems = withSequentialPositions([
          ...swimlaneItems.slice(0, insertIndex),
          {
            ...response,
            name: response?.name || INSERT_CATEGORY_NAME,
            cost: response?.cost ?? 0,
          },
          ...swimlaneItems.slice(insertIndex),
        ]);
        const payload = positionedItems.map(({ id }, position) => ({
          id,
          position,
        }));

        setSwimLaneItems(positionedItems);
        setSwimlaneQueryCache(positionedItems);
        await useReorderLineItem.mutateAsync({ line_items: payload });
      },
      [
        setSwimLaneItems,
        setSwimlaneQueryCache,
        swimlaneItems,
        useReorderLineItem,
      ]
    );

    const persistTopLevelInsertedCategoryOrder = useCallback(
      async ({ item, placement, response }) => {
        const currentRowIndex = topLevelRows.findIndex(
          row => row.type === 'item' && matchesItemId(row.item, item?.id)
        );
        if (currentRowIndex < 0) return;

        const insertRowIndex = clampIndex(
          currentRowIndex + (placement === 'below' ? 1 : 0),
          topLevelRows.length
        );
        const section = getItemSectionId(item);
        const newItem = {
          ...response,
          name: response?.name || INSERT_CATEGORY_NAME,
          cost: response?.cost ?? 0,
          section: response?.section ?? section,
        };
        const rowsWithNewCategory = [...topLevelRows];
        rowsWithNewCategory.splice(insertRowIndex, 0, {
          type: 'item',
          item: newItem,
          renderedCost: calculateRenderedCost(newItem),
        });

        const payload = rowsToRepositionPayload(rowsWithNewCategory);
        const positionedItems = rowsToPositionedItems(rowsWithNewCategory);
        const positionedSections = applySectionPositions(
          topLevelSections,
          payload.sections
        );

        setOptimisticTopLevelSections(positionedSections);
        setSwimLaneItems(positionedItems);
        setSwimlaneQueryCache(positionedItems);
        await useReorderSwimlane.mutateAsync(payload);
      },
      [
        setSwimLaneItems,
        setSwimlaneQueryCache,
        topLevelRows,
        topLevelSections,
        useReorderSwimlane,
      ]
    );

    const handleInsertCategoryRelativeToItem = useCallback(
      async (
        item,
        placement,
        name = INSERT_CATEGORY_NAME,
        editAfterCreate = true
      ) => {
        if (!item?.id || isTemporaryItem(item.id)) return;

        const currentIndex = swimlaneItems.findIndex(swimlaneItem =>
          matchesItemId(swimlaneItem, item.id)
        );
        if (currentIndex < 0) return;

        const insertIndex = clampIndex(
          currentIndex + (placement === 'below' ? 1 : 0),
          swimlaneItems.length
        );
        const currentPosition = Number(item?.position);
        const optimisticPosition = Number.isFinite(currentPosition)
          ? currentPosition + (placement === 'below' ? 0.5 : -0.5)
          : insertIndex;
        const categoryName = String(name || '').trim() || INSERT_CATEGORY_NAME;

        try {
          const response = await handleAddItem(categoryName, false, {
            insertIndex,
            optimisticPosition,
            position: insertIndex,
            section: swimlaneIndex === 0 ? getItemSectionId(item) : undefined,
            normalizePositions: swimlaneIndex !== 0,
          });

          if (!response?.id) return;

          if (swimlaneIndex === 0) {
            await persistTopLevelInsertedCategoryOrder({
              item,
              placement,
              response,
            });
          } else {
            await persistNestedInsertedCategoryOrder({
              item,
              placement,
              response,
            });
          }

          if (editAfterCreate) {
            pendingNameSelectCategoryIdsRef.current.add(response.id.toString());
            setEditing({ [`${swimlaneIndex}-${response.id}`]: true });
          }
          refetch?.();
          refetchBudgetId?.();
        } catch (error) {
          refetch?.();
          refetchBudgetId?.();
        }
      },
      [
        handleAddItem,
        persistNestedInsertedCategoryOrder,
        persistTopLevelInsertedCategoryOrder,
        refetch,
        refetchBudgetId,
        setEditing,
        swimlaneIndex,
        swimlaneItems,
      ]
    );

    const handleInsertCategoriesRelativeToItems = useCallback(
      async (categoryItems, placement) => {
        const targetItems = (categoryItems || []).filter(
          item => item?.id && !isTemporaryItem(item.id)
        );
        if (!targetItems.length) return;

        const insertedItemsByTargetId = {};

        try {
          for (const item of targetItems) {
            const itemIndex = swimlaneItems.findIndex(swimlaneItem =>
              matchesItemId(swimlaneItem, item.id)
            );
            const response = await createSwimlane({
              type,
              budgetId,
              selectedItem,
              name: INSERT_CATEGORY_NAME,
              swimlane,
              section: swimlaneIndex === 0 ? getItemSectionId(item) : undefined,
              position: Math.max(
                0,
                itemIndex + (placement === 'below' ? 1 : 0)
              ),
            }).unwrap();

            insertedItemsByTargetId[getItemId(item).toString()] = {
              ...response,
              name: response?.name || INSERT_CATEGORY_NAME,
              cost: response?.cost ?? 0,
              ...(swimlaneIndex === 0
                ? {
                    section:
                      response?.section ?? getItemSectionId(item) ?? null,
                  }
                : {}),
            };
          }

          if (swimlaneIndex === 0) {
            const insertedRowsByTargetId = Object.fromEntries(
              Object.entries(insertedItemsByTargetId).map(
                ([targetId, insertedItem]) => [
                  targetId,
                  {
                    type: 'item',
                    item: insertedItem,
                    renderedCost: calculateRenderedCost(insertedItem),
                  },
                ]
              )
            );
            const rowsWithInsertedCategories = insertCategoryRowsAdjacent({
              rows: topLevelRows,
              insertedRowsByTargetId,
              placement,
            });
            const payload = rowsToRepositionPayload(rowsWithInsertedCategories);
            const positionedItems = rowsToPositionedItems(
              rowsWithInsertedCategories
            );

            setOptimisticTopLevelSections(
              applySectionPositions(topLevelSections, payload.sections)
            );
            setSwimLaneItems(positionedItems);
            setSwimlaneQueryCache(positionedItems);
            await useReorderSwimlane.mutateAsync(payload);
          } else {
            const positionedItems = withSequentialPositions(
              insertCategoryRowsAdjacent({
                rows: swimlaneItems,
                insertedRowsByTargetId: insertedItemsByTargetId,
                placement,
              })
            );
            const payload = positionedItems.map(({ id }, position) => ({
              id,
              position,
            }));

            setSwimLaneItems(positionedItems);
            setSwimlaneQueryCache(positionedItems);
            await useReorderLineItem.mutateAsync({ line_items: payload });
          }

          refetch?.();
          refetchBudgetId?.();
        } catch (error) {
          refetch?.();
          refetchBudgetId?.();
        }
      },
      [
        budgetId,
        createSwimlane,
        refetch,
        refetchBudgetId,
        selectedItem,
        setSwimLaneItems,
        setSwimlaneQueryCache,
        swimlane,
        swimlaneIndex,
        swimlaneItems,
        topLevelRows,
        topLevelSections,
        type,
        useReorderLineItem,
        useReorderSwimlane,
      ]
    );

    const handleCategoryNameSelected = useCallback(item => {
      if (!item?.id) return;
      pendingNameSelectCategoryIdsRef.current.delete(item.id.toString());
    }, []);

    const handleOptionsChange = item => async action => {
      const value = action?.value || action;
      const targetItems = getMenuTargetItems(item);
      const appliesToMultipleCategories = targetItems.length > 1;

      if (value === INSERT_CATEGORY_ABOVE) {
        if (appliesToMultipleCategories) {
          await handleInsertCategoriesRelativeToItems(targetItems, 'above');
        } else {
          await handleInsertCategoryRelativeToItem(item, 'above');
        }
        return;
      }
      if (value === INSERT_CATEGORY_BELOW) {
        if (appliesToMultipleCategories) {
          await handleInsertCategoriesRelativeToItems(targetItems, 'below');
        } else {
          await handleInsertCategoryRelativeToItem(item, 'below');
        }
        return;
      }
      if (value === 'delete-item') {
        requestCategoryDelete(appliesToMultipleCategories ? targetItems : item);
        return;
      }

      targetItems.forEach(targetItem => {
        handleDropdownAction(value, targetItem);
      });
    };

    useEffect(() => {
      const request = contextualCategorySuggestionRequest;
      if (
        !request?.id ||
        request.targetColumnIndex !== swimlaneIndex ||
        contextualCategorySuggestionHandledRef.current.has(request.id)
      ) {
        return;
      }

      const targetItem = swimlaneItems.find(item =>
        matchesItemId(item, request.targetCategoryId)
      );
      if (!targetItem) return;

      contextualCategorySuggestionHandledRef.current.add(request.id);
      const normalizedName = request.name.trim().toLowerCase();
      const alreadyInColumn = swimlaneItems.some(
        item => item?.name?.trim?.().toLowerCase() === normalizedName
      );
      if (alreadyInColumn) return;

      handleInsertCategoryRelativeToItem(
        targetItem,
        'below',
        request.name,
        false
      );
    }, [
      contextualCategorySuggestionRequest,
      handleInsertCategoryRelativeToItem,
      swimlaneIndex,
      swimlaneItems,
    ]);

    const hideCategoryDeleteConfirm = useCallback(() => {
      if (!isDeletingCategory) {
        setCategoryDeleteTarget(null);
      }
    }, [isDeletingCategory]);

    useEffect(() => {
      const handleCategoryDeleteKeyDown = event => {
        if (event.key !== 'Delete' && event.key !== 'Backspace') return;
        if (!hasEditAccess() || isTextEditingTarget(event.target)) return;

        const focusedSwimlaneItem = event.target?.closest?.('.swimlane-item');
        if (!focusedSwimlaneItem?.id) return;

        const [focusedSwimlaneIndex, focusedItemIndex] =
          focusedSwimlaneItem.id.split(',');
        if (Number(focusedSwimlaneIndex) !== swimlaneIndex) return;

        const item = swimlaneItems?.[Number(focusedItemIndex)];
        if (!item?.id || isTemporaryItem(item.id)) return;

        event.preventDefault();
        event.stopPropagation();
        const targetItems = getMenuTargetItems(item);
        requestCategoryDelete(targetItems.length > 1 ? targetItems : item, {
          skipConfirmation: event.shiftKey,
        });
      };

      document.addEventListener('keydown', handleCategoryDeleteKeyDown);

      return () => {
        document.removeEventListener('keydown', handleCategoryDeleteKeyDown);
      };
    }, [
      getMenuTargetItems,
      hasEditAccess,
      requestCategoryDelete,
      swimlaneIndex,
      swimlaneItems,
    ]);

    const getItemClass = index => {
      return index % 2 === 0 ? 'swimlane-item--even' : 'swimlane-item--odd';
    };

    const canAddSubCategory = item => {
      return (
        item?.swimlane_item_detail_sheets?.length > 0 ||
        item?.swimlane_detail_sheets?.length > 0
      );
    };

    const nextVisibleSelectedItem =
      swimlanes?.[swimlaneIndex + 1]?.selectedItem;

    const activeNavigationItem =
      showDetailedView?.show &&
      Number(showDetailedView?.level) === swimlaneIndex
        ? showDetailedView?.selectedItem
        : nextVisibleSelectedItem;

    const isPresenceInCurrentDetailView = useCallback(
      presence => {
        if (!showDetailedView?.show) return false;

        const selectedId = showDetailedView?.selectedItem?.id;
        const context = presence?.context;
        if (!selectedId || !context) return false;

        if (presence.detail_sheet_type === 'swimlane') {
          return (
            showDetailedView.level === 0 && context.swimlane_id === selectedId
          );
        }

        return (
          showDetailedView.level > 0 && context.line_item_id === selectedId
        );
      },
      [showDetailedView]
    );

    const getPresenceMarkersForItem = useCallback(
      item => {
        if (!item?.id || !presenceBreadcrumbs?.length) return [];

        return presenceBreadcrumbs.filter(presence => {
          if (isPresenceInCurrentDetailView(presence)) return false;

          const context = presence.context;
          if (!context) return false;

          if (type === 'swimlane') {
            if (context.swimlane_id !== item.id) return false;
            return nextVisibleSelectedItem?.id !== item.id;
          }

          const pathIds = context.line_item_path_ids || [];
          if (!pathIds.includes(item.id)) return false;

          return !(
            nextVisibleSelectedItem?.id &&
            pathIds.includes(nextVisibleSelectedItem.id)
          );
        });
      },
      [
        isPresenceInCurrentDetailView,
        nextVisibleSelectedItem,
        presenceBreadcrumbs,
        type,
      ]
    );

    if (!hasEditAccess() && !swimlaneItemsWithCost.length) {
      return null;
    }

    const categoryDroppableId = droppableId || `droppable-${swimlaneIndex}`;

    return (
      <ResizableBox
        axis="x"
        width={swimlaneColumnWidth}
        height={swimlaneHeight - 40}
        minConstraints={[SWIMLANE_COLUMN_MIN_WIDTH, swimlaneHeight - 40]}
        maxConstraints={[SWIMLANE_COLUMN_MAX_WIDTH, swimlaneHeight - 40]}
        onResize={handleSwimlaneColumnResize}
        onResizeStop={handleSwimlaneColumnResizeStop}
      >
        <div className="budget-swimlane">
          <div>
            {selectedCategoryRows.length > 1 && (
              <div className="category-selection-summary">
                <span>{selectedCategoryRows.length} selected</span>
                <strong className="category-selection-summary__total">
                  {formatCurrency(
                    selectedCategoryTotal || 0,
                    budgetData?.primary_currency
                  )}
                </strong>
              </div>
            )}
            <Droppable droppableId={categoryDroppableId}>
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {renderedCategoryRows?.map((row, index) => {
                    if (row.type === 'section') {
                      const sectionId = row.section.id?.toString();
                      return (
                        <TopLevelSectionHeader
                          key={`${TOP_SECTION_PREFIX}${row.section.id}`}
                          section={row.section}
                          index={index}
                          subtotal={sectionSubtotalById[sectionId] || 0}
                          hasEditAccess={hasEditAccess}
                          onOpenSettings={handleOpenSectionSettings}
                        />
                      );
                    }

                    const { item, renderedCost, draggableId } = row;
                    const itemId = draggableId || generateItemId(item);
                    const isActive = activeNavigationItem?.id
                      ? activeNavigationItem.id?.toString() ===
                        item?.id?.toString()
                      : itemId === activeItem?.rowId;
                    const itemClass = getItemClass(index);
                    const menuTargetItems = getMenuTargetItems(item);
                    const allowToAddSubCategory =
                      menuTargetItems.every(canAddSubCategory);
                    const presenceMarkers = getPresenceMarkersForItem(item);
                    const flyFromSuggestionLayoutId =
                      swimlaneIndex === 0 &&
                      isTempId(item.id) &&
                      addedContextualSuggestions[item?.name]
                        ? `suggestion-${item.name}`
                        : null;

                    // Ensure we have a valid draggableId
                    if (!itemId) {
                      return null;
                    }

                    const nestTargetKey = `${categoryDroppableId}:${itemId}`;
                    let nestTargetState = '';
                    if (categoryNestReadyKey === nestTargetKey) {
                      nestTargetState = 'ready';
                    } else if (categoryNestHoverKey === nestTargetKey) {
                      nestTargetState = 'pending';
                    }

                    return (
                      <SwimlaneItem
                        key={itemId}
                        item={item}
                        index={index}
                        itemPositionIndex={swimlaneItems.findIndex(
                          swimlaneItem =>
                            swimlaneItem?.id?.toString() ===
                            item?.id?.toString()
                        )}
                        itemId={itemId}
                        droppableId={categoryDroppableId}
                        nestTargetState={nestTargetState}
                        isActive={isActive}
                        isMultiSelected={selectedCategoryIdSet.has(
                          getItemId(item)?.toString()
                        )}
                        menuTargetCount={menuTargetItems.length}
                        editing={editing}
                        itemClass={itemClass}
                        allowToAddSubCategory={allowToAddSubCategory}
                        lineItemOptions={lineItemOptions}
                        hasEditAccess={hasEditAccess}
                        onItemClick={handleItemClick}
                        onNameChange={handleItemNameChange}
                        onOptionsChange={handleOptionsChange}
                        onSetEditing={setEditing}
                        swimlaneIndex={swimlaneIndex}
                        onKeyUp={onKeyUp}
                        renderedCost={renderedCost}
                        presenceMarkers={presenceMarkers}
                        flyFromSuggestionLayoutId={flyFromSuggestionLayoutId}
                        showAccountNumbers={showAccountNumbers}
                        onAccountNumberChange={handleItemAccountNumberChange}
                        selectNameOnEdit={pendingNameSelectCategoryIdsRef.current.has(
                          item?.id?.toString()
                        )}
                        onNameSelected={handleCategoryNameSelected}
                      />
                    );
                  })}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <AddItemSection
              showAddItemInput={showAddItemInput}
              hasEditAccess={hasEditAccess}
              hasDetailsSheet={hasDetailsSheet}
              swimlaneIndex={swimlaneIndex}
              swimlaneItems={swimlaneItems}
              addItemSuggestions={addItemSuggestions}
              onAddItemClick={handleAddItemClick}
              onAddItem={handleAddItemWithCallback}
              onAddDetailedView={addDetailedView}
              onAddTopLevelSection={
                swimlaneIndex === 0 ? handleCreateTopLevelSection : null
              }
              selectedItem={selectedItem}
            />
          </div>
          {/* Contractual Charges - only show at the top level (swimlaneIndex === 0) */}
          {swimlaneIndex === 0 && (
            <div className="budget-adjustments-section">
              <ContractualChargesSection budgetId={budgetId} />
              <FilmIncentivesSection budgetId={budgetId} />
            </div>
          )}
          <SectionSettingsModal
            show={Boolean(sectionSettingsTarget)}
            section={sectionSettingsTarget}
            subtotal={
              sectionSubtotalById[getSectionId(sectionSettingsTarget)] || 0
            }
            categories={
              sectionCategoriesById[getSectionId(sectionSettingsTarget)] || []
            }
            draftName={sectionNameDraft}
            canEdit={hasEditAccess()}
            selectTitleOnOpen={selectSectionTitleOnOpen}
            onDraftNameChange={setSectionNameDraft}
            onSave={handleSaveSection}
            onDelete={handleDeleteTopLevelSection}
            onHide={handleCloseSectionSettings}
            isSaving={updateTopLevelSectionMutation.isLoading}
            isDeleting={deleteTopLevelSectionMutation.isLoading}
          />
          <DeleteConfirmModal
            showModal={Boolean(categoryDeleteTarget)}
            handleOnClose={hideCategoryDeleteConfirm}
            onCancel={hideCategoryDeleteConfirm}
            onConfirm={() => deleteCategory(categoryDeleteTarget)}
            confirmText={
              categoryDeleteTarget?.categoryCount > 1
                ? `Are you sure you want to delete these ${categoryDeleteTarget.categoryCount} categories?`
                : 'Are you sure you want to delete this category?'
            }
            itemName={
              <span>
                <strong>{categoryDeleteTarget?.name}</strong>
                <br />
                This will delete {categoryDeleteTarget?.lineItemCount || 0}{' '}
                {(categoryDeleteTarget?.lineItemCount || 0) === 1
                  ? 'line item'
                  : 'line items'}{' '}
                totaling {formatCurrency(categoryDeleteTarget?.total || 0)}.
              </span>
            }
            confirmLabel="Delete"
            disabled={isDeletingCategory}
          />
        </div>
      </ResizableBox>
    );
  }
);

SwimlaneRefactored.displayName = 'SwimlaneRefactored';

SwimlaneRefactored.propTypes = {
  swimlaneHeight: PropTypes.number,
  selectedItem: PropTypes.object,
  type: PropTypes.oneOf(['swimlane', 'line-item', 'line-item-children'])
    .isRequired,
  onKeyUp: PropTypes.func.isRequired,
  editing: PropTypes.object.isRequired,
  setEditing: PropTypes.func.isRequired,
  swimlane: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  swimlaneIndex: PropTypes.number.isRequired,
  handleAddSwimLanes: PropTypes.func.isRequired,
  addItemSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  addDetailedView: PropTypes.func.isRequired,
  budgetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  idCat: PropTypes.any,
  clearSwimlaneInfo: PropTypes.func,
  presenceBreadcrumbs: PropTypes.array,
  budgetData: PropTypes.object,
  droppableId: PropTypes.string,
  categoryNestHoverKey: PropTypes.string,
  categoryNestReadyKey: PropTypes.string,
  activeSelectionColumnIndex: PropTypes.number,
  onCategorySelectionActivated: PropTypes.func,
  onColumnWidthChange: PropTypes.func,
  contextualCategorySuggestionRequest: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    targetCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    targetColumnIndex: PropTypes.number.isRequired,
  }),
  suggestionScopeKey: PropTypes.string,
  onSuggestionDeleted: PropTypes.func,
  onCategoryColumnItemsChange: PropTypes.func,
};

SwimlaneRefactored.defaultProps = {
  swimlaneHeight: 500,
  addItemSuggestions: null,
  swimlane: null,
  selectedItem: {},
  budgetId: '',
  idCat: undefined,
  clearSwimlaneInfo: null,
  presenceBreadcrumbs: [],
  budgetData: null,
  droppableId: '',
  categoryNestHoverKey: '',
  categoryNestReadyKey: '',
  activeSelectionColumnIndex: null,
  onCategorySelectionActivated: null,
  onColumnWidthChange: null,
  contextualCategorySuggestionRequest: null,
  suggestionScopeKey: 'category:root',
  onSuggestionDeleted: null,
  onCategoryColumnItemsChange: null,
};

export default SwimlaneRefactored;
